import moment from 'moment';
import { htmlToJson } from '@contentstack/json-rte-serializer';

import { readArchive } from '../../../util/archive';
import { sequence } from '../../../util/promises';

import { DATE_FORMAT_OUTPUT, MESSAGES_BLOCK_COUNT, MESSAGE_ATTACHMENTS_SEPARATOR, MESSAGE_SYSTEM_SENDER_NAME, getBundledMessage } from '../ConversationActions';
import { escapeRegExp } from '../../../util/text';
import { getLinksInMessage } from '../BookActions';

function readTextEntry(file, onProgress) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            onProgress(100);
            resolve([
                {
                    filename: file.name,
                    path: file.name,
                    content: reader.result,
                },
            ]);
        };
        reader.onerror = reject;
        reader.readAsText(file);
    });
}

// Read selected file by user
// For Messenger: User must select an archive
export function readFile(file, options, onProgress) {
    const fileType = `${file.type || ''}`.toLowerCase();
    const fileName = `${file.name || ''}`;
    const isZip = fileType.includes('zip') || /\.zip$/i.test(fileName);

    if(!isZip && (fileType.includes('html') || /\.html$/i.test(fileName))) {
        return readTextEntry(file, onProgress);
    }
    if(!isZip && (fileType.includes('json') || /\.json$/i.test(fileName))) {
        return readTextEntry(file, onProgress);
    }

    // Zip My Data : le JSON liste toutes les conversations. On évite les HTML
    // snap_history qui partagent le même nom de fichier que chat_history.
    return readArchive(
        file,
        { ...options, includeRegexFiles: [/(^|\/)chat_history\.json$/i, /(^|\/)snap_history\.json$/i] },
        percentile => onProgress(percentile * 0.5),
    ).then(jsonEntries => {
        if(jsonEntries && jsonEntries.length) {
            onProgress(100);
            return jsonEntries;
        }
        return readArchive(
            file,
            { ...options, includeRegexFiles: [/(^|\/)chat_history\/subpage_[^/]+\.html$/i] },
            percentile => onProgress(50 + (percentile * 0.5)),
        );
    });
}

export function getFiles(file, filesPaths = [], onProgress = () => {}) {
    return new Promise((resolve, reject) => {
        // console.log('FB getFiles', filesPaths);
        // console.log('local images', filesPaths.filter(imagePath => !/http(s)?:\/\//.test(imagePath)));
        // console.log('distant images', filesPaths.filter(imagePath => /http(s)?:\/\//.test(imagePath)));
        const localImages = filesPaths.filter(imagePath => !/http(s)?:\/\//.test(imagePath));
        readArchive(
            file,
            { includeRegexFiles: [
                ...localImages.map(imagePath => new RegExp(escapeRegExp(imagePath.split('/').pop()), 'gi')),
            ] },
            onProgress,
        ).then(localFiles => {
            console.log('Local files done', localFiles);

            return readArchive(
                file,
                { includeRegexFiles: filesPaths.filter(imagePath => /http(s)?:\/\//.test(imagePath)).map(imagePath => new RegExp(getMediaFileName(imagePath).split('.').shift(), 'gi')) },
                onProgress,
            ).then(distantFiles => {
                console.log('Distant files done', distantFiles);
                resolve([
                    ...(localFiles || []),
                    ...(distantFiles || []).map(distantImage => {
                        const ext = distantImage.filename.split('.').pop();
                        const filename = getMediaFileName(filesPaths.find(imagePath => {
                            const imagePathParts = distantImage.filename.split('_');
                            imagePathParts.pop();
                            return imagePath.includes(imagePathParts.join('_'));
                        }) || '');
                        return {
                            ...distantImage,
                            filename,
                        };
                    }),
                ]);
            }).catch(reject);
        }).catch(reject);
    });
}

export function canHandleImages(file) {
    return false;
}

function parseHtmlEntry(entry) {
    console.log('Snapchat::: parseHtmlEntry', entry.filename);
    const htmlDomBody = new DOMParser().parseFromString(entry.content, 'text/html').body;
    const data = htmlToJson(htmlDomBody);
    // console.log(data);
    return data;
}

function searchElementInHtml(htmlData, attributeSearch = '', attributeValueSearch = '', attributeTarget = '', options = { includes: false, allowMultiple: false }) {
    // console.log('1 - Search for', htmlData, attributeSearch, attributeValueSearch, attributeTarget, options.includes);
    const searchIn = elt => {
        // console.log('2 - Search in ', elt, typeof elt);
        if(!elt || (!Array.isArray(elt) && typeof elt !== 'object')) {
            return null;
        }
        const attributeTest = (!attributeSearch && !attributeValueSearch) || options.includes ? ((elt?.attrs || {})[attributeSearch] || '').includes(attributeValueSearch) : (elt?.attrs || {})[attributeSearch] === attributeValueSearch;
        if(attributeTest) {
            // console.log('4 - Attribute found', elt[attributeTarget]);
            return elt[attributeTarget];
        }
        // Array.isArray(elt)
        // ? console.log('3 - ', 'Search in sub array', elt)
        // : console.log('3 - ', 'Search in sub object', Object.keys(elt).map(key => elt[key]));

        return (
            elt.children
            ? elt.children.map(sub => searchIn(sub))
            : [] // Object.keys(elt).map(key => searchIn(elt[key])).filter(elt => elt)
        ).flat();
    };
    let r = searchIn(htmlData);
    if(!options.allowMultiple && Array.isArray(r)) {
        // console.log('Single value expected', r);
        r = r && r[0];
    }
    // console.log('X - Result', attributeSearch, r);
    return r;
}

function getEntryKey(entry) {
    return String(entry?.path || entry?.filename || '').replace(/\\/g, '/');
}

function isChatHistoryJson(entry) {
    return /(^|\/)chat_history\.json$/i.test(getEntryKey(entry));
}

function isSnapHistoryJson(entry) {
    return /(^|\/)snap_history\.json$/i.test(getEntryKey(entry));
}

function isSnapchatJson(entry) {
    return isChatHistoryJson(entry) || isSnapHistoryJson(entry);
}

function isSnapchatNoisyStatus(mediaType) {
    return /^(STATUSERASED|STATUSCONVERSATIONCAPTURESCREENSHOT|STATUSSNAPPOSTOPENVIEWINGPOLICY|STATUSSAVETOCAMERAROLL)/i.test(String(mediaType || '').trim());
}

function isSnapchatStatusType(mediaType) {
    return /^STATUS/i.test(String(mediaType || '').trim());
}

function snapchatPlaceholderForType(mediaType) {
    const type = String(mediaType || '').trim().toUpperCase();
    if(type === 'NOTE') {
        return getBundledMessage('conversationSnapchatPlaceholderNote', '[Voice note]');
    }
    if(type === 'STICKER') {
        return getBundledMessage('conversationSnapchatPlaceholderSticker', '[Sticker]');
    }
    if(type === 'IMAGE' || type === 'VIDEO' || type === 'SNAP') {
        return getBundledMessage('conversationSnapchatPlaceholderSnap', '[Snap]');
    }
    if(type === 'AUDIO') {
        return getBundledMessage('conversationSnapchatPlaceholderAudio', '[Audio]');
    }
    if(type === 'MEDIA') {
        return getBundledMessage('conversationSnapchatPlaceholderMedia', '[Media]');
    }
    return '';
}

function snapchatStatusLabel(mediaType) {
    const type = String(mediaType || '').trim().toUpperCase();
    if(type === 'STATUSPARTICIPANTADDED') {
        return getBundledMessage('conversationSnapchatStatusParticipantAdded', 'A member was added to the group');
    }
    if(type === 'STATUSNAMECHANGED') {
        return getBundledMessage('conversationSnapchatStatusNameChanged', 'The group name changed');
    }
    if(type === 'STATUSCONVERSATIONCREATED') {
        return getBundledMessage('conversationSnapchatStatusConversationCreated', 'Conversation created');
    }
    return getBundledMessage('conversationSnapchatStatusGeneric', 'Snapchat notification');
}

function snapchatMessageText(content, mediaType) {
    const text = content == null ? '' : String(content);
    if(text.trim()) {
        return text;
    }
    return snapchatPlaceholderForType(mediaType);
}

function parseChatHistoryJson(entry) {
    try {
        const data = JSON.parse(entry.content);
        if(data && typeof data === 'object' && !Array.isArray(data)) {
            return data;
        }
    } catch(err) {
        console.error('Snapchat JSON parse failed', err);
    }
    return null;
}

function mergeJsonThreadMap(target, data) {
    Object.keys(data || {}).forEach(threadId => {
        if(!Array.isArray(data[threadId])) {
            return;
        }
        target[threadId] = (target[threadId] || []).concat(data[threadId]);
    });
}

function getJsonConversations(entries) {
    const merged = {};
    (entries || []).filter(isSnapchatJson).forEach(entry => {
        const data = parseChatHistoryJson(entry);
        if(!data) {
            return;
        }
        mergeJsonThreadMap(merged, data);
    });
    return merged;
}

function getJsonThreadName(threadId, messages) {
    const titled = (messages || []).find(message => message && message['Conversation Title']);
    return (titled && titled['Conversation Title']) || threadId;
}

function parseSnapchatDate(raw) {
    const created = String((raw && raw.Created) || '');
    const dateParts = created.split(' ');
    if(dateParts[0] && dateParts[1]) {
        return moment.utc(`${dateParts[0]} ${dateParts[1]}`, 'YYYY-MM-DD HH:mm:ss').format();
    }
    if(raw && raw['Created(microseconds)']) {
        return moment.utc(raw['Created(microseconds)'], 'x').format();
    }
    return '';
}

function mapJsonMessage(raw) {
    const mediaType = raw['Media Type'] || raw.MediaType || '';
    if(isSnapchatNoisyStatus(mediaType)) {
        return null;
    }
    const date = parseSnapchatDate(raw);
    if(!date) {
        return null;
    }
    const isSystem = isSnapchatStatusType(mediaType);
    const from = isSystem ? MESSAGE_SYSTEM_SENDER_NAME : (raw.From || '');
    if(!from) {
        return null;
    }
    const originalText = raw.Content == null ? '' : String(raw.Content);
    return {
        from,
        date,
        content: isSystem ? snapchatStatusLabel(mediaType) : snapchatMessageText(raw.Content, mediaType),
        medias: [],
        mediaType,
        isSystem,
        isSaved: Boolean(raw.IsSaved),
        hasText: Boolean(originalText.trim()),
        microseconds: raw['Created(microseconds)'] || 0,
    };
}

function keepSnapchatMessages(mapped) {
    return (mapped || []).filter(message => message && message.from && message.date && message.content);
}

function messagesFromJsonThread(rawMessages) {
    const mapped = keepSnapchatMessages((rawMessages || []).map(mapJsonMessage));
    return mapped.sort((a, b) => (a.microseconds || 0) - (b.microseconds || 0) || (new Date(a.date) - new Date(b.date)));
}

function getJsonThreadStats(rawMessages) {
    const messages = messagesFromJsonThread(rawMessages);
    return {
        count: messages.length,
        contentCount: (rawMessages || []).filter(message => message && String(message.Content || '').trim()).length,
        savedCount: (rawMessages || []).filter(message => message && message.IsSaved).length,
    };
}

function isSnapchatSnapHtml(entry) {
    return /\/snap_history\//i.test(getEntryKey(entry));
}

function isSnapchatChatHtml(entry) {
    const key = getEntryKey(entry);
    if(!/\.html$/i.test(key) || isSnapchatSnapHtml(entry)) {
        return false;
    }
    // Zip My Data : uniquement les sous-pages de chat, jamais snap_history.
    if(/\/chat_history\//i.test(key) || /\/snap_history\//i.test(key)) {
        return /\/chat_history\/subpage_[^/]+\.html$/i.test(key);
    }
    // Fichier HTML déposé seul (subpage_<uuid>.html, chat.html, etc.).
    return true;
}

// Titres Snapchat localisés : "Chat History with X", "Historique de chat avec X", etc.
function extractSnapchatThreadName(title) {
    const raw = String(title || '').replace(/\s+/g, ' ').trim();
    if(!raw) {
        return '';
    }
    const prefixed = raw.match(/^(.*?)\s+(?:with|avec|mit|con|met|com)\s+(.+)$/i);
    if(prefixed && prefixed[2]) {
        return prefixed[2].trim();
    }
    return raw;
}

function getThreadNameFromHtml(data) {
    const wrapper = searchElementInHtml(data, 'class-name', 'rightpanel', 'children', { allowMultiple: true }) || [];
    const header = wrapper[0] || {};
    const titleNode = (header.children || [])[1] || (header.children || [])[0] || {};
    const threadTitleBrut = ((titleNode.children || [])[0] || {}).text || titleNode.text || '';
    !threadTitleBrut && console.log('Thread: Missingtitle', data, threadTitleBrut);
    return extractSnapchatThreadName(threadTitleBrut);
}

function htmlEntryMatchesThread(entry, thread, htmlEntriesCount = 0) {
    if(!isSnapchatChatHtml(entry) || !entry?.content) {
        return false;
    }
    const key = getEntryKey(entry);
    const tracked = thread?.filenames || [];
    if(tracked.length) {
        return tracked.includes(key) || tracked.includes(entry.filename);
    }
    if(htmlEntriesCount === 1) {
        return true;
    }
    const name = getThreadNameFromHtml(parseHtmlEntry(entry));
    return Boolean(name && (name === thread?.id || name === thread?.name));
}

function collectNodeTexts(node, texts = []) {
    if(!node) {
        return texts;
    }
    if(node.text && String(node.text).trim()) {
        texts.push(String(node.text).trim());
    }
    (node.children || []).forEach(child => collectNodeTexts(child, texts));
    return texts;
}

function getHtmlMessageType(wrapper) {
    const header = (wrapper.children || [])[0];
    const typeHost = (header?.children || [])[1] || header;
    const token = collectNodeTexts(typeHost).find(text => /^[A-Z][A-Z0-9_]+$/.test(text));
    return token || '';
}

function getMessagesListFromHtml(data, includeService = false) {
    const messages = searchElementInHtml(data, 'class-name', 'rightpanel', 'children', { allowMultiple: true });
    if(!Array.isArray(messages)) {
        return [];
    }
    return messages.map(messageHtml => {
        const wrapper = (messageHtml.children || [])[0];
        if(wrapper?.attrs?.orgType === 'span') {
            const dateText = (((wrapper.children[wrapper.children.length - 1] || {}).children || [])[0] || {}).text || '';
            const dateBrut = String(dateText).split(' ');
			const day = dateBrut[0];
			const time = dateBrut[1];
            if(day && time) {
				const fromBase = (((((wrapper.children || [])[0] || {}).children || [])[0] || {}).children || []);
				const from = (((fromBase[1] || fromBase[0] || {}).children || [])[0] || {}).text || '';
				!from && console.log('Missing message from', fromBase);
                const mediaType = getHtmlMessageType(wrapper);
                if(isSnapchatNoisyStatus(mediaType)) {
                    return null;
                }
                const rawContent = ((((wrapper.children[wrapper.children.length - 2] || {}).children || [])[0] || {})).text || '';
                const isSystem = isSnapchatStatusType(mediaType);
                return {
                    content: isSystem ? snapchatStatusLabel(mediaType) : snapchatMessageText(rawContent, mediaType),
                    date: dateBrut && moment.utc(`${day} ${time}`, 'YYYY-MM-DD HH:mm:ss').format(),
                    from: isSystem ? MESSAGE_SYSTEM_SENDER_NAME : from,
                    medias: [],
                    mediaType,
                    isSystem,
                    hasText: Boolean(String(rawContent || '').trim()),
                };
            }
        }
        console.log('Missing sub span message wrapper', wrapper, wrapper?.attrs?.orgType);
        return null;
    }).filter(message => message);

    // return (data?.html?.body?.div?.div[1]?.div?.div?.filter(messageWrapper => incluseService || !messageWrapper['@_class'].includes('service')) || [])
    //     .map(messageHtml => {
    //     const sender = searchElementInHtml(messageHtml, '@_class', 'from_name', '#text');
    //     prevSender = sender || prevSender;
    //     const message = {
    //         html: messageHtml,
    //         content: (searchElementInHtml(messageHtml, '@_class', 'text', '#text', { allowMultiple: true }) || []).join('\r\n') || '',
    //         date: moment.utc(searchElementInHtml(messageHtml, '@_class', 'date', '@_title', { includes: true }), 'DD.MM.YYYY HH:mm:ss').format().split('UTC').shift(),
    //         from: sender || prevSender || '',
    //         medias: getMessageMediasFromHtml(messageHtml) || [],
    //     };
    //     if(!message.from || !message.date) {
    //         console.log(message);
    //     }
    //     return message;
    // });
}

// function getMessageMediasFromHtml(messageHtml) {
//     const wrappers = searchElementInHtml(messageHtml, '@_class', 'media_wrap', 'a', { includes: true, allowMultiple: true });
//     return wrappers.map(wrapper => {
//         const file = searchElementInHtml(wrapper, '', '', '@_href');
//         if(!file) {
//             // console.log('Not a valid file', file, messageHtml, wrapper, links);
//             return null;
//         }
//         let type = '';
//         if(file.includes('video') || ['.mov'].includes((file || '').toLowerCase())) {
//             type = 'video';
//         } else if(file.includes('audio') || ['.ogg'].includes((file || '').toLowerCase())) {
//             type = 'audio';
//         } else if(file.includes('photo')) {
//             type = 'image';
//         } else {
//             console.log('Not a valid media', file);
//         }

//         return type && {
//             type,
//             file,
//         };
//     }).filter(media => media);
// }

// Retrieve profile name
export function getProfileName(entries) {
    const jsonConversations = getJsonConversations(entries);
    const jsonThreadIds = Object.keys(jsonConversations);
    if(jsonThreadIds.length) {
        for(let i = 0; i < jsonThreadIds.length; i += 1) {
            const owner = (jsonConversations[jsonThreadIds[i]] || []).find(message => message && message.IsSender && message.From);
            if(owner && owner.From) {
                return decodeURIComponent(encodeURIComponent(owner.From));
            }
        }
    }

    const participants = [];
    entries.filter(isSnapchatChatHtml).forEach(entry => {
        const data = parseHtmlEntry(entry);
        const threadName = getThreadNameFromHtml(data);
        getMessagesListFromHtml(data).forEach(message => {
            const messageSender = message.from;
            if(messageSender && messageSender !== MESSAGE_SYSTEM_SENDER_NAME && !participants.find(participant => participant === messageSender) && messageSender !== threadName) {
                participants.push(messageSender);
            }
        });
    });
    return decodeURIComponent(encodeURIComponent(participants && participants.length ? participants.shift() : ''));
}

// Retrieve threads names
// friendThread = {
//     id: '',
//     name: '',
//     count: 0,
// };
export function getThreads(entries) {
    const jsonConversations = getJsonConversations(entries);
    const jsonThreadIds = Object.keys(jsonConversations);
    if(jsonThreadIds.length) {
        const threads = jsonThreadIds.map(threadId => {
            const stats = getJsonThreadStats(jsonConversations[threadId]);
            return {
                id: threadId,
                name: getJsonThreadName(threadId, jsonConversations[threadId]),
                count: stats.count,
                contentCount: stats.contentCount,
                savedCount: stats.savedCount,
            };
        }).filter(thread => thread.count > 0);
        console.log('Threads found', threads);
        return threads;
    }

    let threads = [];

    entries.filter(isSnapchatChatHtml).forEach(entry => {
        console.log('Search threads in', entry);
        const data = parseHtmlEntry(entry);
        const name = getThreadNameFromHtml(data);
        const filename = getEntryKey(entry);
        if(name) {
			const listed = keepSnapchatMessages(getMessagesListFromHtml(data));
            const count = listed?.length;
            const contentCount = listed.filter(message => message.hasText).length;
            if(threads.find(thread => thread.name === name)) {
                threads = threads.map(thread => {
                    if(thread.name === name) {
                        const filenames = thread.filenames || [];
                        return {
                            ...thread,
                            count: thread.count + count,
                            contentCount: (thread.contentCount || 0) + contentCount,
                            filenames: filename && !filenames.includes(filename)
                                ? [...filenames, filename]
                                : filenames,
                        };
                    }
                    return thread;
                });
            } else {
                threads.push({
                    id: name,
                    name,
                    count,
                    contentCount,
                    filenames: filename ? [filename] : [],
                });
            }
        }
    });
    console.log('Threads found', threads);
    return threads;
}

// Generate CSV Content
// Separate messages in distinct csv (10 000 messages / csv)
export function getArchiveContent(entries, from, thread, options = { withImages: false, withMedias: false }, onProgress = percentile => {}) {
    console.log(thread, from, options, entries);

    const participants = [];
    let messages = [];
    const messagesBlocks = [];
    let images = [];
    let medias = [];

    const jsonConversations = getJsonConversations(entries);
    if(Object.keys(jsonConversations).length) {
        const rawMessages = jsonConversations[thread.id] || jsonConversations[thread.name] || [];
        messages = messagesFromJsonThread(rawMessages);
        onProgress(1);
    } else {
        const htmlEntries = entries.filter(isSnapchatChatHtml);

        entries
        .filter(entry => htmlEntryMatchesThread(entry, thread, htmlEntries.length))
        .forEach(entry => {
            onProgress(1); // Init progress

            const data = parseHtmlEntry(entry);
            messages = messages.concat(getMessagesListFromHtml(data));
        });
        messages = keepSnapchatMessages(messages);
    }

	console.log('Messages found 1', [...messages]);

    messages = messages
        // .sort((a, b) => getMessageDateFromHtml(a) - getMessageDateFromHtml(b))
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .map((message, index) => {
            return {
                ...message,
                ...getMessageContent(message, from, thread.name, options),
            };
        });
        // .filter(({ messageMedias }) => {
        //     if(process.env.NODE_ENV === 'development') {
        //         return messageMedias.length && messageMedias.find(media => media.includes('.mp4'));
        //     }
        //     return true;
        // })

	console.log('Error messages', messages.filter(message => !message.messageCsv));

	messages = messages.filter(messageData => {
            // !messageData.messageCsv && console.error('Message not valid', messageData);
            // messageData.messageImages.length && console.log(messageData.messageImages, entries, entries.some(entry => messageData.messageImages.some(imagePath => imagePath && `${entry.path}`.toLowerCase().includes(imagePath.toLowerCase()))));
            // @TODO: Check if image file is in entries (image entries)
            return messageData.messageCsv;
        });

    const iteration = Math.floor(messages.length / MESSAGES_BLOCK_COUNT);

    const percentile = 100 / (iteration + 1);

    console.log(`${[...messages].length} messages to check`, [...messages]);

    for(let i = 0; i <= iteration; i += 1) {
        console.log(`Messages block process: ${i + 1}/${iteration + 1} (de ${i * MESSAGES_BLOCK_COUNT} à ${i * MESSAGES_BLOCK_COUNT + MESSAGES_BLOCK_COUNT})`);
        const messagesBlock = messages.slice(i * MESSAGES_BLOCK_COUNT, i * MESSAGES_BLOCK_COUNT + MESSAGES_BLOCK_COUNT);

        onProgress(percentile);

        let csv = '';
        messagesBlock.forEach((message, j) => { // eslint-disable-line
            const { messageParticipant, messageCsv, messageImages, messageMedias } = message;

            if(!participants.includes(messageParticipant) && messageParticipant !== MESSAGE_SYSTEM_SENDER_NAME) {
                participants.push(messageParticipant);
            }
            csv += messageCsv;

            csv += '\n';
            images = images.concat(messageImages);
            medias = medias.concat(messageMedias);
        });

        if(messagesBlock[0]) {
			console.log('New messages block to add', 'start', messagesBlock[0].date, new Date(messagesBlock[0].date), 'end', messagesBlock[messagesBlock.length - 1].date, new Date((messagesBlock[messagesBlock.length - 1] || messagesBlock[0]).date));
            messagesBlocks.push({
                sort: i,
                filename: `messages_${i + 1}.csv`,
                content: csv,
                messages: messagesBlock,
                start: new Date(messagesBlock[0].date),
                end: new Date((messagesBlock[messagesBlock.length - 1] || messagesBlock[0]).date),
            });
        } else {
            console.error('Missing messages in block', messagesBlock, participants);
        }
    }

    return {
        participants,
        messagesBlocks,
        images,
        medias,
    };
}

function getMessageContent(message, from, to, options = { withImages: false, withMedias: false }) {
    let messageCsv = '';
    const medias = [...getMessageMedias(message, 'link')];

    const messageImages = getMessageMedias(message, 'image');
    const messageMedias = getMessageMedias(message, ['audio', 'video']);

    if(options.withImages) {
        medias.push(...messageImages);
    }
    if(options.withMedias) {
        medias.push(...messageMedias);
    }

    const isSystem = Boolean(message.isSystem);
    const senderName = isSystem ? MESSAGE_SYSTEM_SENDER_NAME : message.from;
    if(!senderName) {
        console.log('Missing sender name', message);
        return null;
    }

	const date = message.date && moment.utc(message.date).format(DATE_FORMAT_OUTPUT).split('+').shift();

    const messageContent = message.content;
    !messageContent && console.log('Missing message content', senderName, messageContent, message);

    const data = {
        date,
        sender: senderName,
        to: isSystem ? from : (senderName === from ? to : from),
        content: messageContent ? encodeURIComponent(decodeURI(encodeURI(messageContent))) : '',
        isSender: !isSystem && senderName === from ? 'true' : 'false',
        medias: (medias && medias.length ? medias.map(media => getMediaFileName(media)).join(MESSAGE_ATTACHMENTS_SEPARATOR) : ''),
    };

    messageCsv = date && senderName ? `${Object.keys(data).map(key => data[key]).join(';')};` : '';

    return { messageParticipant: senderName, messageCsv, messageImages, messageMedias, date };
}

// Retrieve profile name
function getMessageMedias(message, types = null) {
    if(types && !Array.isArray(types)) {
        types = [types];
    }
    if(!types || !types.length) {
        types = null;
    }

	const medias = [];

	if(!types || types.includes('link')) {
		medias.push(...(getLinksInMessage(message.content) || []));
	}

    return [
		...medias,
		...message.medias.filter(media => !types || types.includes(media.type)).map(media => (media.file || '').toLowerCase()),
	];
}

function getMediaFileName(path) {
    return path.split('?').shift().split('/').pop();
}
